import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, Flex, Modal, Stack, Text } from '@mantine/core';

import { usePatchBoardInvitation } from 'api/boards';
import { useShowNotification } from 'hooks';

type Props = {
  opened: boolean;
  onClose: () => void;
  invitationId: string;
};

export const InvitationDeleteModal = ({
  opened,
  onClose,
  invitationId,
}: Props) => {
  const { organisationId, boardId } = useParams() as {
    organisationId: string;
    boardId: string;
  };
  const [isDeletePending, setIsDeletePending] = useState(false);
  const { mutateAsync: patchBoardInvitation } = usePatchBoardInvitation();
  const showNotification = useShowNotification();
  const { t } = useTranslation('organisations');

  const deleteHandler = async () => {
    setIsDeletePending(true);

    try {
      await patchBoardInvitation({
        path: { organisationId, boardId, invitationId },
      });
      showNotification({
        variant: 'success',
        message: t(
          'organisationView.boards.invitations.delete.modal.successMessage',
        ),
      });
    } catch (error) {
      showNotification({
        variant: 'error',
        message: t(
          'organisationView.boards.invitations.delete.modal.errorMessage',
        ),
      });
    }
    onClose();
    setIsDeletePending(false);
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      centered
      size="md"
      title={t('organisationView.boards.invitations.delete.modal.title')}
    >
      <Stack spacing="xl">
        <Text>
          {t('organisationView.boards.invitations.delete.modal.description')}
        </Text>
        <Flex justify="flex-end" gap="lg">
          <Button variant="default" onClick={onClose}>
            {t('organisationView.boards.invitations.delete.modal.cancelButton')}
          </Button>
          <Button color="red" onClick={deleteHandler} loading={isDeletePending}>
            {t('organisationView.boards.invitations.delete.modal.deleteButton')}
          </Button>
        </Flex>
      </Stack>
    </Modal>
  );
};
